import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, isBefore, startOfDay } from "date-fns";
import { ChevronLeft, ChevronRight, Ship, Calendar, User, Wrench, AlertTriangle } from "lucide-react";
import { PriorityBadge } from "./PMSConstants";

interface PMSCalendarProps {
  vessels: any[];
  accessibleVessels: any[];
  jobs: any[];
  vesselFilter: string;
  onVesselFilterChange: (v: string) => void;
  isChiefEngineer: boolean;
  isTechnicalManager: boolean;
  userId?: string;
  crewMembers: { user_id: string; full_name: string; pms_role: string; vessel_id: string }[];
  onAssignJob: (jobId: string, userId: string, userName: string) => void;
  onSelectJob: (job: any) => void;
}

const JOB_COLOR_MAP: Record<string, { bg: string; border: string; text: string; label: string }> = {
  not_assigned: { bg: "bg-muted/60", border: "border-muted-foreground/20", text: "text-muted-foreground", label: "Not Assigned" },
  assigned: { bg: "bg-blue-100 dark:bg-blue-500/15", border: "border-blue-300 dark:border-blue-500/30", text: "text-blue-700 dark:text-blue-400", label: "Assigned" },
  in_progress: { bg: "bg-amber-100 dark:bg-amber-500/15", border: "border-amber-300 dark:border-amber-500/30", text: "text-amber-700 dark:text-amber-400", label: "In Progress" },
  overdue: { bg: "bg-red-100 dark:bg-red-500/15", border: "border-red-300 dark:border-red-500/30", text: "text-red-700 dark:text-red-400", label: "Overdue" },
  completed: { bg: "bg-emerald-100 dark:bg-emerald-500/15", border: "border-emerald-300 dark:border-emerald-500/30", text: "text-emerald-700 dark:text-emerald-400", label: "Completed" },
  deferred: { bg: "bg-purple-100 dark:bg-purple-500/15", border: "border-purple-300 dark:border-purple-500/30", text: "text-purple-700 dark:text-purple-400", label: "Deferred" },
};

function getJobCalendarStatus(job: any): string {
  if (job.status === "completed") return "completed";
  if (job.status === "deferred") return "deferred";
  if (job.status === "in_progress") return "in_progress";
  if (job.status === "overdue" || (job.status === "scheduled" && job.next_due_date && isBefore(new Date(job.next_due_date), startOfDay(new Date())))) return "overdue";
  if (job.assigned_to) return "assigned";
  return "not_assigned";
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const PMSCalendar = ({
  vessels, accessibleVessels, jobs, vesselFilter, onVesselFilterChange,
  isChiefEngineer, isTechnicalManager, userId, crewMembers, onAssignJob, onSelectJob,
}: PMSCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [assignDialogJob, setAssignDialogJob] = useState<any>(null);

  const canAssign = isChiefEngineer || isTechnicalManager;

  // Filter jobs by vessel
  const filteredJobs = useMemo(() => {
    let j = jobs.filter((job: any) => job.next_due_date);
    if (vesselFilter !== "all") j = j.filter((job: any) => job.vessel_id === vesselFilter);
    return j;
  }, [jobs, vesselFilter]);

  // Build calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad to start on Monday
  const startDayOfWeek = monthStart.getDay(); // 0=Sun
  const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
  const prevMonthEnd = endOfMonth(subMonths(currentMonth, 1));
  const paddedStart = eachDayOfInterval({
    start: new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), prevMonthEnd.getDate() - paddingDays + 1),
    end: prevMonthEnd,
  });

  // Pad end
  const endDayOfWeek = monthEnd.getDay();
  const endPadding = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
  const nextMonthStart = addMonths(monthStart, 1);
  const paddedEnd = endPadding > 0 ? eachDayOfInterval({
    start: nextMonthStart,
    end: new Date(nextMonthStart.getFullYear(), nextMonthStart.getMonth(), endPadding),
  }) : [];

  const allDays = [...paddedStart, ...calendarDays, ...paddedEnd];

  // Group jobs by date
  const jobsByDate = useMemo(() => {
    const map = new Map<string, any[]>();
    filteredJobs.forEach((job: any) => {
      const dateKey = format(new Date(job.next_due_date), "yyyy-MM-dd");
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(job);
    });
    return map;
  }, [filteredJobs]);

  // Stats
  const stats = useMemo(() => {
    const s = { total: filteredJobs.length, not_assigned: 0, assigned: 0, in_progress: 0, overdue: 0, completed: 0 };
    filteredJobs.forEach(j => {
      const status = getJobCalendarStatus(j);
      if (status === "not_assigned") s.not_assigned++;
      else if (status === "assigned") s.assigned++;
      else if (status === "in_progress") s.in_progress++;
      else if (status === "overdue") s.overdue++;
      else if (status === "completed") s.completed++;
    });
    return s;
  }, [filteredJobs]);

  const selectedDayJobs = selectedDay ? (jobsByDate.get(format(selectedDay, "yyyy-MM-dd")) || []) : [];

  // Get crew for assignment (filtered by vessel if selected)
  const assignableCrew = useMemo(() => {
    if (!assignDialogJob) return crewMembers;
    return crewMembers.filter(c => c.vessel_id === assignDialogJob.vessel_id);
  }, [crewMembers, assignDialogJob]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="font-display text-lg min-w-[180px] text-center">{format(currentMonth, "MMMM yyyy")}</h2>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-xs text-teal" onClick={() => setCurrentMonth(new Date())}>Today</Button>
        </div>
        <Select value={vesselFilter} onValueChange={onVesselFilterChange}>
          <SelectTrigger className="w-56 h-8 text-xs">
            <SelectValue placeholder="All Vessels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all"><div className="flex items-center gap-2"><Ship className="w-3 h-3" />All Vessels</div></SelectItem>
            {accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Legend + Stats */}
      <div className="flex flex-wrap gap-3 items-center">
        {Object.entries(JOB_COLOR_MAP).filter(([k]) => k !== "deferred").map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm border ${val.bg} ${val.border}`} />
            <span className="text-[10px] text-muted-foreground">{val.label}</span>
            <span className="text-[10px] font-bold text-foreground">
              {key === "not_assigned" ? stats.not_assigned : key === "assigned" ? stats.assigned : key === "in_progress" ? stats.in_progress : key === "overdue" ? stats.overdue : stats.completed}
            </span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b">
            {WEEKDAYS.map(d => (
              <div key={d} className="p-2 text-center text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7">
            {allDays.map((day, i) => {
              const dateKey = format(day, "yyyy-MM-dd");
              const dayJobs = jobsByDate.get(dateKey) || [];
              const inMonth = isSameMonth(day, currentMonth);
              const today = isToday(day);
              const isSelected = selectedDay && isSameDay(day, selectedDay);

              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(day)}
                  className={`min-h-[90px] border-b border-r p-1 text-left transition-colors ${
                    !inMonth ? "bg-muted/20 opacity-50" : ""
                  } ${today ? "bg-teal/5" : ""} ${isSelected ? "ring-2 ring-teal ring-inset" : "hover:bg-muted/30"}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`text-[11px] font-medium ${today ? "bg-teal text-white rounded-full w-5 h-5 flex items-center justify-center" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>
                      {format(day, "d")}
                    </span>
                    {dayJobs.length > 3 && <span className="text-[9px] text-muted-foreground">+{dayJobs.length - 3}</span>}
                  </div>
                  <div className="space-y-0.5">
                    {dayJobs.slice(0, 3).map((job: any) => {
                      const calStatus = getJobCalendarStatus(job);
                      const color = JOB_COLOR_MAP[calStatus] || JOB_COLOR_MAP.not_assigned;
                      const isMyJob = job.assigned_to === userId;
                      return (
                        <div
                          key={job.id}
                          onClick={(e) => { e.stopPropagation(); onSelectJob(job); }}
                          className={`px-1 py-0.5 rounded text-[9px] truncate border cursor-pointer ${color.bg} ${color.border} ${color.text} ${isMyJob ? "font-bold ring-1 ring-teal/50" : ""}`}
                          title={`${job.title} — ${job.vessel_name}${job.assigned_to_name ? ` (${job.assigned_to_name})` : ""}`}
                        >
                          {job.title}
                        </div>
                      );
                    })}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Detail */}
      {selectedDay && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal" />
              {format(selectedDay, "EEEE, d MMMM yyyy")}
              <Badge variant="outline" className="text-[9px] ml-2">{selectedDayJobs.length} job{selectedDayJobs.length !== 1 ? "s" : ""}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {selectedDayJobs.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No jobs scheduled for this day</p>
            ) : (
              selectedDayJobs.map((job: any) => {
                const calStatus = getJobCalendarStatus(job);
                const color = JOB_COLOR_MAP[calStatus] || JOB_COLOR_MAP.not_assigned;
                const isMyJob = job.assigned_to === userId;
                return (
                  <div key={job.id} className={`flex items-center gap-3 p-3 rounded-lg border ${color.bg} ${color.border} ${isMyJob ? "ring-1 ring-teal" : ""}`}>
                    <Wrench className={`w-4 h-4 shrink-0 ${color.text}`} />
                    <div className="flex-1 min-w-0">
                      <button className="text-left" onClick={() => onSelectJob(job)}>
                        <p className="text-xs font-display truncate">{job.title}</p>
                        <p className="text-[10px] text-muted-foreground">{job.job_number} · {job.vessel_name} · {job.department}</p>
                      </button>
                      {job.assigned_to_name && (
                        <div className="flex items-center gap-1 mt-1">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <span className="text-[10px] text-muted-foreground">{job.assigned_to_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <PriorityBadge priority={job.priority} />
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${color.bg} ${color.text} border ${color.border}`}>{color.label}</span>
                      {canAssign && (
                        <Button size="sm" variant="outline" className="h-6 text-[9px]" onClick={(e) => { e.stopPropagation(); setAssignDialogJob(job); }}>
                          {job.assigned_to ? "Reassign" : "Assign"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}

      {/* Assign Dialog */}
      <Dialog open={!!assignDialogJob} onOpenChange={(o) => { if (!o) setAssignDialogJob(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Assign Job to Crew Member</DialogTitle>
          </DialogHeader>
          {assignDialogJob && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-display">{assignDialogJob.title}</p>
                <p className="text-[10px] text-muted-foreground">{assignDialogJob.job_number} · {assignDialogJob.vessel_name}</p>
                {assignDialogJob.assigned_to_name && (
                  <p className="text-[10px] text-amber-600 mt-1">Currently assigned to: {assignDialogJob.assigned_to_name}</p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium mb-2">Select Crew Member</p>
                {assignableCrew.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">No crew assigned to this vessel. Assign crew via Vessel Access first.</p>
                ) : (
                  <ScrollArea className="max-h-[300px]">
                    <div className="space-y-1">
                      {assignableCrew.map((crew) => (
                        <button
                          key={`${crew.user_id}-${crew.vessel_id}`}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors border ${
                            assignDialogJob.assigned_to === crew.user_id ? "border-teal bg-teal/10" : "border-border hover:bg-muted/50"
                          }`}
                          onClick={() => {
                            onAssignJob(assignDialogJob.id, crew.user_id, crew.full_name);
                            setAssignDialogJob(null);
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center text-xs font-bold text-teal">
                            {crew.full_name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-display">{crew.full_name}</p>
                            <p className="text-[10px] text-muted-foreground capitalize">{crew.pms_role.replace("_", " ")}</p>
                          </div>
                          {assignDialogJob.assigned_to === crew.user_id && (
                            <Badge className="text-[8px] bg-teal">Current</Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PMSCalendar;
